import type { CandidateState, TaskId } from '../domain/types';

/**
 * Welches Blatt gerade über dem Bogen liegt. Es ist immer höchstens eines offen.
 *
 * Liegt in einer eigenen Datei, getrennt von `useAppStore.ts`: `hydrate.ts` braucht `Sheet` und
 * `normalizeSheet`, `useAppStore.ts` braucht `Sheet` für seinen eigenen Zustand — läge beides in
 * `useAppStore.ts` oder in `hydrate.ts`, ergäbe sich ein Ringschluss zwischen Store und
 * Persistenz. Hier hängen beide Seiten nur noch an dieser einen, neutralen Datei.
 */
export type Sheet =
  | { kind: 'none' }
  | { kind: 'setup' }
  | { kind: 'settings' }
  | { kind: 'candidates' }
  | { kind: 'vignette' }
  | { kind: 'timer' }
  | { kind: 'comment'; taskId: TaskId }
  /** Der Kommentar zur **Station** — dasselbe Blatt, anderer Bezug (seit 2026-09-13). */
  | { kind: 'stationComment' }
  | { kind: 'submit' }
  | { kind: 'locked' }
  | { kind: 'scanner' }
  /** Punktzahl einer Aufgabe von Hand setzen. */
  | { kind: 'score'; taskId: TaskId }
  /** Stufe eines bewerteten Kriteriums wählen. */
  | { kind: 'criterion'; taskId: TaskId; index: number };

/** Wo das offene Blatt liegt — Ansichtssache, deshalb `localStorage` und nicht die Datenbank. */
export const SHEET_KEY = 'ui.sheet';

/**
 * Welches Blatt beim Wiedereinstieg wieder aufgehen darf. `comment` bleibt, alles Flüchtige
 * fällt weg — ginge der Scanner nach einem Absturz wieder auf, stünde die Kamera vor dem Bogen.
 * `locked` ergibt sich ohnehin neu aus `CandidateExamMetadata.status`.
 */
export function normalizeSheet(sheet: unknown): Sheet {
  if (!sheet || typeof sheet !== 'object') return { kind: 'none' };
  const kind = (sheet as { kind?: unknown }).kind;
  const taskId = (sheet as { taskId?: unknown }).taskId;
  if (kind === 'comment' && typeof taskId === 'string') return { kind: 'comment', taskId };
  return { kind: 'none' };
}

/**
 * Der Zustand eines Prüflings. Die Anzeige führt eigene deutsche Schlüssel, Relay die
 * Werteliste von `CandidateExamMetadata.status`. Hier steht die Abbildung — an einer Stelle,
 * nicht verstreut.
 */
export type RelayStatus = 'NOT_STARTED' | 'RUNNING' | 'PAUSED' | 'SUBMITTED' | 'ABORTED';

const NACH_ANZEIGE: Record<RelayStatus, CandidateState> = {
  NOT_STARTED: 'wartet',
  RUNNING: 'laeuft',
  // PAUSED hat im Bogen noch keine eigene Anzeige; „läuft" ist die ehrlichste Näherung.
  PAUSED: 'laeuft',
  SUBMITTED: 'bewertet',
  ABORTED: 'entschuldigt',
};

export const stateFromStatus = (status: RelayStatus): CandidateState => NACH_ANZEIGE[status];

export const statusFromState = (state: CandidateState): RelayStatus =>
  state === 'wartet' ? 'NOT_STARTED'
  : state === 'laeuft' ? 'RUNNING'
  : state === 'bewertet' ? 'SUBMITTED'
  : 'ABORTED';
